import api from './api';
import { translateError } from '../utils/errorTranslations';
import { formatCurrency, formatBTC, formatPercentage } from '../utils/formatters';

// Cache para dados históricos (evita recarregar a cada simulação)
let historicalDataCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Carrega dados históricos do Bitcoin da API
 * @returns {Promise<Object>} Objeto com dados históricos { "YYYY-MM-DD": price }
 */
const loadHistoricalBitcoinData = async () => {
  try {
    // Verificar se o cache ainda é válido
    const now = Date.now();
    if (historicalDataCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      console.log('✅ Using cached historical Bitcoin data:', Object.keys(historicalDataCache).length, 'records');
      return historicalDataCache;
    }

    console.log('📡 Loading fresh historical Bitcoin data from API...');
    
    // ✅ Buscar dados históricos da API
    const response = await api.get('/historical-data/');
    
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error('Invalid response format from historical data API');
    }
    
    console.log(`📊 Received ${response.data.length} historical data points from API`);
    
    // Converter array da API para objeto { "YYYY-MM-DD": price }
    const historicalData = {};
    response.data.forEach(item => {
      if (item.date && item.close) {
        // Normalizar formato da data para YYYY-MM-DD
        const date = new Date(item.date);
        const dateStr = date.toISOString().split('T')[0];
        historicalData[dateStr] = item.close;
      }
    });
    
    const dataCount = Object.keys(historicalData).length;
    if (dataCount === 0) {
      throw new Error('No valid historical data received from API');
    }
    
    // Atualizar cache
    historicalDataCache = historicalData;
    cacheTimestamp = now;
    
    const dates = Object.keys(historicalData).sort();
    console.log(`✅ Successfully loaded and cached ${dataCount} historical data points`);
    console.log(`📅 Date range: ${dates[0]} to ${dates[dates.length - 1]}`);
    
    return historicalData;
    
  } catch (error) {
    console.error('Error loading historical Bitcoin data from CSV:', error);
    throw new Error(`Failed to load historical Bitcoin data: ${error.message}`);
  }
};

/**
 * Limpa o cache de dados históricos (útil para forçar recarregamento)
 */
export const clearHistoricalDataCache = () => {
  historicalDataCache = null;
  cacheTimestamp = null;
  console.log('Historical data cache cleared');
};

/**
 * Interpola preço entre duas datas usando interpolação linear
 * @param {Date} targetDate - Data para interpolar
 * @param {Object} historicalData - Dados históricos do Bitcoin
 * @returns {number|null} Preço interpolado ou null se não for possível
 */
const interpolatePrice = (targetDate, historicalData) => {
  const dates = Object.keys(historicalData).sort();
  const targetTime = targetDate.getTime();
  
  // Encontrar 2 datas mais próximas (antes e depois)
  let beforeDate = null;
  let afterDate = null;
  
  for (const date of dates) {
    const dateTime = new Date(date).getTime();
    if (dateTime < targetTime) {
      beforeDate = date;
    } else if (dateTime > targetTime) {
      afterDate = date;
      break;
    }
  }
  
  // Precisa ter uma data antes E uma depois para interpolar
  if (!beforeDate || !afterDate) {
    return null;
  }
  
  const beforeTime = new Date(beforeDate).getTime();
  const afterTime = new Date(afterDate).getTime();
  const beforePrice = historicalData[beforeDate];
  const afterPrice = historicalData[afterDate];
  
  // Validar preços
  if (typeof beforePrice !== 'number' || typeof afterPrice !== 'number' || 
      beforePrice <= 0 || afterPrice <= 0) {
    return null;
  }
  
  // Interpolação linear: price = price_before + (price_after - price_before) * ratio
  const ratio = (targetTime - beforeTime) / (afterTime - beforeTime);
  const interpolatedPrice = beforePrice + (afterPrice - beforePrice) * ratio;
  
  const daysBefore = Math.round((targetTime - beforeTime) / (24 * 60 * 60 * 1000));
  const daysAfter = Math.round((afterTime - targetTime) / (24 * 60 * 60 * 1000));
  
  console.log(`📈 Interpolated price for ${targetDate.toISOString().split('T')[0]}: $${interpolatedPrice.toFixed(2)} (between ${beforeDate} [$${beforePrice.toFixed(2)}] and ${afterDate} [$${afterPrice.toFixed(2)}], ${daysBefore}d before, ${daysAfter}d after)`);
  
  return interpolatedPrice;
};

/**
 * Obtém preço histórico do Bitcoin para uma data específica
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {Object} historicalData - Dados históricos do Bitcoin (objeto direto)
 * @returns {number} Preço do Bitcoin na data
 * @throws {Error} Se não encontrar preço para a data
 */
const getHistoricalPrice = (date, historicalData) => {
  // ❌ FAIL FAST: Sem dados históricos = ERRO
  if (!historicalData || typeof historicalData !== 'object') {
    throw new Error(`No historical data available for date: ${date}`);
  }
  
  // ❌ FAIL FAST: Data inválida = ERRO
  const targetDate = new Date(date);
  if (isNaN(targetDate.getTime())) {
    throw new Error(`Invalid date format: ${date}`);
  }
  
  const targetDateStr = targetDate.toISOString().split('T')[0];
  
  // 1. Buscar data exata
  if (historicalData[targetDateStr]) {
    return historicalData[targetDateStr];
  }
  
  // 2. Verificar se a data está fora do range de dados históricos
  const availableDates = Object.keys(historicalData).sort();
  const firstDate = availableDates[0];
  const lastDate = availableDates[availableDates.length - 1];
  const firstDateTime = new Date(firstDate).getTime();
  const lastDateTime = new Date(lastDate).getTime();
  const targetDateTime = targetDate.getTime();
  
  // Se a data é anterior aos dados históricos, usar o primeiro preço disponível
  if (targetDateTime < firstDateTime) {
    console.warn(`⚠️ Transaction date ${targetDateStr} is before historical data range (${firstDate}). Using earliest available price.`);
    return historicalData[firstDate];
  }
  
  // Se a data é posterior aos dados históricos, usar o último preço disponível
  if (targetDateTime > lastDateTime) {
    console.warn(`⚠️ Transaction date ${targetDateStr} is after historical data range (${lastDate}). Using latest available price.`);
    return historicalData[lastDate];
  }
  
  // 3. ✅ NOVA PRIORIDADE: Tentar interpolação linear PRIMEIRO
  const interpolated = interpolatePrice(targetDate, historicalData);
  if (interpolated !== null) {
    return interpolated;
  }
  
  // 4. Buscar data mais próxima (dentro de 14 dias ao invés de 7)
  let closestPrice = null;
  let closestDate = null;
  let minDiff = Infinity;
  const MAX_DAYS_DIFF = 14 * 24 * 60 * 60 * 1000; // ✅ 14 dias ao invés de 7
  
  for (const [dateStr, price] of Object.entries(historicalData)) {
    // Validar preço
    if (typeof price !== 'number' || price <= 0) {
      console.warn(`⚠️ Invalid price in historical data: ${dateStr} = ${price}`);
      continue;
    }
    
    const diff = Math.abs(new Date(dateStr) - targetDate);
    if (diff < minDiff && diff <= MAX_DAYS_DIFF) {
      minDiff = diff;
      closestPrice = price;
      closestDate = dateStr;
    }
  }
  
  // 5. Se encontrou preço próximo, usar
  if (closestPrice !== null) {
    const daysDiff = Math.round(minDiff / (24 * 60 * 60 * 1000));
    if (daysDiff > 0) {
      console.log(`📅 Using price from ${daysDiff} day(s) away: ${targetDateStr} → ${closestDate}`);
    }
    return closestPrice;
  }
  
  // ❌ FAIL FAST: Não encontrou preço = ERRO (mas agora é MUITO mais raro)
  throw new Error(
    `No historical price found for ${targetDateStr}. ` +
    `Available data range: ${firstDate} to ${lastDate}. ` +
    `Tried interpolation and closest match within 14 days.`
  );
};

/**
 * Cria linha temporal contínua interpolando preços entre transações
 * @param {Array} dcaTransactions - Transações DCA simuladas
 * @param {Array} userTransactions - Transações reais do usuário
 * @param {Object} historicalData - Dados históricos de preços
 * @param {string} interval - 'daily' ou 'weekly'
 * @returns {Array} Timeline com pontos interpolados
 * @throws {Error} Se faltar dados históricos para alguma data
 */
const createContinuousTimeline = (dcaTransactions, userTransactions, historicalData, interval = 'weekly') => {
  // ✅ ROBUSTEZ: Validação de entrada
  if (!Array.isArray(dcaTransactions) || !Array.isArray(userTransactions)) {
    console.error('❌ Invalid transactions arrays provided to createContinuousTimeline');
    return [];
  }
  
  if (dcaTransactions.length === 0 && userTransactions.length === 0) {
    console.warn('⚠️ No transactions provided to createContinuousTimeline');
    return [];
  }
  
  // 1. Identificar período total
  const allDates = [...dcaTransactions, ...userTransactions]
    .map(tx => tx && tx.date ? new Date(tx.date) : null)
    .filter(date => date !== null && !isNaN(date.getTime()));
  
  if (allDates.length === 0) {
    console.error('❌ No valid dates found in transactions');
    return [];
  }
  
  const startDate = new Date(Math.min(...allDates));
  const endDate = new Date(Math.max(...allDates));
  
  console.log(`📊 Creating continuous timeline from ${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]} (${interval})`);
  console.log(`   DCA transactions: ${dcaTransactions.length}, User transactions: ${userTransactions.length}`);
  
  // 2. Mapear transações por data para acesso rápido
  const dcaMap = {};
  const userMap = {};
  
  console.log(`   Mapping ${dcaTransactions.length} DCA transactions...`);
  dcaTransactions.forEach((tx) => {
    const txDate = new Date(tx.date);
    const dateKey = txDate.toISOString().split('T')[0];
    dcaMap[dateKey] = tx;
  });
  
  console.log(`   Mapping ${userTransactions.length} User transactions...`);
  userTransactions.forEach((tx) => {
    const txDate = new Date(tx.date);
    const dateKey = txDate.toISOString().split('T')[0];
    if (!userMap[dateKey]) {
      userMap[dateKey] = [];
    }
    userMap[dateKey].push(tx);
  });
  
  console.log(`   DCA unique dates: ${Object.keys(dcaMap).length}`);
  console.log(`   User unique dates: ${Object.keys(userMap).length}`);
  console.log(`   First 3 DCA dates: ${Object.keys(dcaMap).slice(0, 3).join(', ')}`);
  console.log(`   First 3 User dates: ${Object.keys(userMap).slice(0, 3).join(', ')}`);
  
  // 3. Criar timeline contínua incluindo TODAS as datas de transações
  const timeline = [];
  const incrementDays = interval === 'weekly' ? 7 : 1;
  
  // Criar array com TODAS as datas: transações + pontos intermediários semanais
  const allTimelineDates = new Set();
  
  // Adicionar todas as datas de transações
  Object.keys(dcaMap).forEach(date => allTimelineDates.add(date));
  Object.keys(userMap).forEach(date => allTimelineDates.add(date));
  
  // Adicionar pontos intermediários semanais
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const dateKey = currentDate.toISOString().split('T')[0];
    allTimelineDates.add(dateKey);
    currentDate.setDate(currentDate.getDate() + incrementDays);
  }
  
  // Converter para array e ordenar cronologicamente
  const sortedDates = Array.from(allTimelineDates).sort();
  
  const transactionDatesCount = Object.keys(dcaMap).length + Object.keys(userMap).length;
  const intermediateDatesCount = sortedDates.length - transactionDatesCount;
  
  console.log(`   Timeline composition:`);
  console.log(`   - Transaction dates: ${transactionDatesCount} (${Object.keys(dcaMap).length} DCA + ${Object.keys(userMap).length} User)`);
  console.log(`   - Intermediate points: ${intermediateDatesCount}`);
  console.log(`   - Total dates: ${sortedDates.length}`);
  console.log(`   - Date range: ${sortedDates[0]} to ${sortedDates[sortedDates.length-1]}`);
  
  // Variáveis acumuladas
  let dcaCumulativeBtc = 0;
  let dcaCumulativeInvested = 0;
  let userCumulativeBtc = 0;
  let userCumulativeInvested = 0;
  let userCumulativeCash = 0;
  
  let transactionsProcessed = 0;
  
  // Iterar sobre TODAS as datas em ordem cronológica
  for (const dateKey of sortedDates) {
    // Atualizar acumulados DCA se houver transação nesta data
    if (dcaMap[dateKey]) {
      dcaCumulativeBtc += dcaMap[dateKey].bitcoin;
      dcaCumulativeInvested += dcaMap[dateKey].value;
      transactionsProcessed++;
    }
    
    // Atualizar acumulados do usuário se houver transações nesta data
    if (userMap[dateKey]) {
      // eslint-disable-next-line no-loop-func
      userMap[dateKey].forEach(tx => {
        const txType = tx.type?.toLowerCase();
        
        if (txType === 'sell') {
          // VENDAS: Subtrair Bitcoin e adicionar cash
          const bitcoinSold = tx.btc_sold || 0;
          const revenue = tx.revenue_from_sale || tx.value || 0;
          userCumulativeBtc -= bitcoinSold;
          userCumulativeCash += revenue;
          transactionsProcessed++;
          // Não alterar investimento - vendas são conversão BTC→Cash
        } else if (txType === 'buy' || txType === 'reinvestment') {
          // COMPRAS: Adicionar Bitcoin e investimento
          
          // 🔥 CORREÇÃO CRÍTICA: Usar btc_acquired do backend
          const btcAcquired = tx.btc_acquired || tx.quantity || (tx.value / tx.price);
          userCumulativeBtc += btcAcquired;
          userCumulativeInvested += tx.value;
          transactionsProcessed++;
        }
      });
    }
    
    // ❌ FAIL FAST: Obter preço histórico (lança erro se não encontrar)
    try {
      const price = getHistoricalPrice(dateKey, historicalData);
      
      if (typeof price !== 'number' || price <= 0) {
        throw new Error(`Invalid price returned for ${dateKey}: ${price}`);
      }
      // Calcular ROI
      const dcaCurrentValue = dcaCumulativeBtc * price;
      const userCurrentValue = (userCumulativeBtc * price) + userCumulativeCash;
      
      const dcaRoi = dcaCumulativeInvested > 0 
        ? ((dcaCurrentValue - dcaCumulativeInvested) / dcaCumulativeInvested) * 100 
        : 0;
      const userRoi = userCumulativeInvested > 0 
        ? ((userCurrentValue - userCumulativeInvested) / userCumulativeInvested) * 100 
        : 0;
      
      timeline.push({
        date: dateKey,
        dcaBtc: dcaCumulativeBtc,
        dcaInvested: dcaCumulativeInvested,
        dcaValue: dcaCurrentValue,
        dcaRoi: dcaRoi,
        userBtc: userCumulativeBtc,
        userInvested: userCumulativeInvested,
        userValue: userCurrentValue,
        userCash: userCumulativeCash,
        userRoi: userRoi,
        roiDifference: userRoi - dcaRoi,
        price: price,
        hasTransaction: !!dcaMap[dateKey] || !!userMap[dateKey] // Flag para identificar pontos reais
      });
    } catch (error) {
      // ❌ FAIL FAST: Re-lançar erro com contexto
      throw new Error(
        `Failed to get historical price for timeline at ${dateKey}: ${error.message}`
      );
    }
  }
  
  console.log(`✅ Timeline created successfully:`);
  console.log(`   - Total points: ${timeline.length}`);
  console.log(`   - Transactions processed: ${transactionsProcessed} (expected: ${dcaTransactions.length + userTransactions.length})`);
  console.log(`   - Processing rate: ${(transactionsProcessed / (dcaTransactions.length + userTransactions.length) * 100).toFixed(1)}%`);
  
  // Verificar ordenação e duplicatas
  let orderingIssues = 0;
  for (let i = 1; i < timeline.length; i++) {
    if (timeline[i].date <= timeline[i-1].date) {
      orderingIssues++;
      if (orderingIssues <= 3) {
        console.warn(`⚠️ Ordering issue at index ${i}: ${timeline[i-1].date} -> ${timeline[i].date}`);
      }
    }
  }
  if (orderingIssues > 0) {
    console.error(`❌ Found ${orderingIssues} ordering issues in timeline!`);
  } else {
    console.log(`   ✓ Timeline is properly ordered chronologically`);
  }
  
  // Log dos primeiros e últimos 3 pontos para debug
  if (timeline.length > 0) {
    console.log(`   First 3 points:`);
    timeline.slice(0, 3).forEach(p => {
      console.log(`   ${p.date}: DCA=${p.dcaBtc.toFixed(8)} BTC ($${p.dcaValue.toFixed(2)}), User=${p.userBtc.toFixed(8)} BTC ($${p.userValue.toFixed(2)})`);
    });
    
    console.log(`   Last 3 points:`);
    timeline.slice(-3).forEach(p => {
      console.log(`   ${p.date}: DCA=${p.dcaBtc.toFixed(8)} BTC ($${p.dcaValue.toFixed(2)}), User=${p.userBtc.toFixed(8)} BTC ($${p.userValue.toFixed(2)})`);
    });
  }
  
  return timeline;
};

/**
 * Valida dados de transação
 * @param {Object} transaction - Transação a ser validada
 * @returns {boolean} True se a transação é válida
 */
const validateTransaction = (transaction) => {
  if (!transaction || typeof transaction !== 'object') {
    return false;
  }
  
  // Verificar campos obrigatórios
  if (!transaction.date || !transaction.type) {
    return false;
  }
  
  // Verificar se a data é válida
  const date = new Date(transaction.date);
  if (isNaN(date.getTime())) {
    return false;
  }
  
  // Verificar tipos de transação válidos
  const validTypes = ['buy', 'sell', 'reinvestment', 'investment', 'aporte', 'recompra'];
  if (!validTypes.includes(transaction.type.toLowerCase())) {
    return false;
  }
  
  // Verificar valores numéricos
  const numericFields = ['quantity', 'price', 'value', 'btc_acquired', 'btc_sold', 'revenue_from_sale', 'recommended_investment', 'reinvestment_with_cash'];
  for (const field of numericFields) {
    if (transaction[field] !== undefined && transaction[field] !== null) {
      const value = parseFloat(transaction[field]);
      if (isNaN(value) || value < 0) {
        console.warn(`Invalid ${field} in transaction:`, transaction[field]);
        return false;
      }
    }
  }
  
  return true;
};

/**
 * Otimiza dados de transação para melhor performance na simulação
 * @param {Array} transactions - Transações originais
 * @returns {Array} Transações otimizadas
 */
const optimizeTransactionsForSimulation = (transactions) => {
  if (transactions.length <= 100) {
    // Para poucos dados, não precisa otimizar
    return transactions;
  }
  
  console.log(`Optimizing ${transactions.length} transactions for simulation...`);
  
  // Agrupar transações por semana para reduzir complexidade
  const weeklyGroups = {};
  
  transactions.forEach(tx => {
    const date = new Date(tx.date);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay()); // Início da semana (domingo)
    const weekKey = weekStart.toISOString().split('T')[0];
    
    if (!weeklyGroups[weekKey]) {
      weeklyGroups[weekKey] = [];
    }
    weeklyGroups[weekKey].push(tx);
  });
  
  // Consolidar transações por semana
  const optimizedTransactions = [];
  
  Object.entries(weeklyGroups).forEach(([weekKey, weekTransactions]) => {
    if (weekTransactions.length === 1) {
      // Se há apenas uma transação na semana, manter como está
      optimizedTransactions.push(weekTransactions[0]);
    } else {
      // Consolidar múltiplas transações da mesma semana
      const consolidated = consolidateWeeklyTransactions(weekTransactions, weekKey);
      if (consolidated) {
        optimizedTransactions.push(consolidated);
      }
    }
  });
  
  console.log(`Optimized to ${optimizedTransactions.length} consolidated transactions`);
  return optimizedTransactions.sort((a, b) => new Date(a.date) - new Date(b.date));
};

/**
 * Consolida transações de uma semana em uma única transação
 * @param {Array} weekTransactions - Transações da semana
 * @param {string} weekKey - Chave da semana
 * @returns {Object} Transação consolidada
 */
const consolidateWeeklyTransactions = (weekTransactions, weekKey) => {
  const buyTxs = weekTransactions.filter(tx => tx.type === 'buy' || tx.type === 'reinvestment');
  const sellTxs = weekTransactions.filter(tx => tx.type === 'sell');
  
  if (buyTxs.length === 0 && sellTxs.length === 0) {
    return null;
  }
  
  // Consolidar compras
  let totalBtcAcquired = 0;
  let totalInvested = 0;
  let totalReinvestment = 0;
  let weightedPrice = 0;
  let totalQuantity = 0;
  
  buyTxs.forEach(tx => {
    const btcAcquired = parseFloat(tx.btc_acquired || tx.quantity || 0);
    const invested = parseFloat(tx.recommended_investment || tx.value || 0);
    const reinvestment = parseFloat(tx.reinvestment_with_cash || 0);
    const price = parseFloat(tx.price || 0);
    const quantity = parseFloat(tx.quantity || 0);
    
    totalBtcAcquired += btcAcquired;
    totalInvested += invested;
    totalReinvestment += reinvestment;
    totalQuantity += quantity;
    
    if (price > 0 && quantity > 0) {
      weightedPrice += price * quantity;
    }
  });
  
  // Consolidar vendas
  let totalBtcSold = 0;
  let totalRevenue = 0;
  
  sellTxs.forEach(tx => {
    totalBtcSold += parseFloat(tx.btc_sold || tx.quantity || 0);
    totalRevenue += parseFloat(tx.revenue_from_sale || tx.value || 0);
  });
  
  // Calcular preço médio ponderado
  const avgPrice = totalQuantity > 0 ? weightedPrice / totalQuantity : 0;
  
  // Determinar tipo principal da transação consolidada
  let consolidatedType = 'buy';
  if (sellTxs.length > buyTxs.length) {
    consolidatedType = 'sell';
  } else if (totalReinvestment > totalInvested) {
    consolidatedType = 'reinvestment';
  }
  
  return {
    id: `consolidated_${weekKey}`,
    date: weekKey,
    type: consolidatedType,
    quantity: totalBtcAcquired - totalBtcSold,
    price: avgPrice,
    value: totalInvested + totalReinvestment - totalRevenue,
    btc_acquired: totalBtcAcquired,
    btc_sold: totalBtcSold,
    revenue_from_sale: totalRevenue,
    recommended_investment: totalInvested,
    reinvestment_with_cash: totalReinvestment,
    is_consolidated: true,
    original_transactions_count: weekTransactions.length
  };
};

/**
 * Obtém dados para o relatório de desempenho
 * @param {string} username - Nome do usuário
 * @param {number} currentPrice - Preço atual do Bitcoin
 * @returns {Promise} Promessa com os dados do relatório
 */
export const getPerformanceReport = async (username, currentPrice) => {
  try {
    // Obter dados do portfólio
    const portfolioResponse = await api.get(`/portfolio/${username}`, {
      params: { current_price: currentPrice }
    });
    
    // Obter histórico de transações
    const transactionsResponse = await api.get(`/transactions/${username}`);
    
    // Combinar dados em um relatório de desempenho
    return {
      portfolio: portfolioResponse.data,
      transactions: transactionsResponse.data,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Erro ao obter relatório de desempenho:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao carregar relatório de desempenho'));
  }
};

/**
 * Exporta dados do portfólio para CSV
 * @param {Object} portfolioData - Dados do portfólio
 * @returns {string} Conteúdo do arquivo CSV
 */
export const exportPortfolioToCSV = (portfolioData) => {
  if (!portfolioData) return '';
  
  let csvContent = 'Metric,Value\n';
  csvContent += `Date,${new Date().toLocaleDateString()}\n`;
  csvContent += `BTC Balance,${portfolioData.btc_balance}\n`;
  csvContent += `Total Invested,${portfolioData.total_invested}\n`;
  csvContent += `Current Portfolio Value,${portfolioData.portfolio_value}\n`;
  csvContent += `Profit/Loss,${portfolioData.profit_loss}\n`;
  csvContent += `Profit/Loss Percentage,${portfolioData.profit_loss_percentage}\n`;
  csvContent += `Average Purchase Price,${portfolioData.average_buy_price || portfolioData.avg_purchase_price}\n`;
  csvContent += `Current Bitcoin Price,${portfolioData.current_price}\n`;
  
  return csvContent;
};

/**
 * Exporta dados completos do portfólio incluindo transações para CSV
 * @param {Object} portfolioData - Dados do portfólio
 * @param {Array} transactions - Lista de transações
 * @returns {string} Conteúdo do arquivo CSV
 */
export const exportCompletePortfolioToCSV = (portfolioData, transactions) => {
  if (!portfolioData) {
    return '';
  }
  
  let csvContent = '';
  
  // Seção 1: Resumo do Portfólio
  csvContent += '=== PORTFOLIO SUMMARY ===\n';
  csvContent += 'Metric,Value\n';
  csvContent += `Export Date,${new Date().toLocaleDateString('en-US')}\n`;
  csvContent += `BTC Balance,${parseFloat(portfolioData.btc_balance || 0).toFixed(8)}\n`;
  csvContent += `Total Invested (USD),${parseFloat(portfolioData.total_invested || 0).toFixed(2)}\n`;
  csvContent += `Current Portfolio Value (USD),${parseFloat(portfolioData.portfolio_value || 0).toFixed(2)}\n`;
  csvContent += `Profit/Loss (USD),${parseFloat(portfolioData.profit_loss || 0).toFixed(2)}\n`;
  csvContent += `Profit/Loss Percentage,${parseFloat(portfolioData.profit_loss_percentage || 0).toFixed(4)}%\n`;
  csvContent += `Average Purchase Price (USD),${parseFloat(portfolioData.average_buy_price || portfolioData.avg_purchase_price || 0).toFixed(2)}\n`;
  csvContent += `Current Bitcoin Price (USD),${parseFloat(portfolioData.current_price || 0).toFixed(2)}\n`;
  csvContent += '\n';
  
  // Seção 2: Transações Individuais (com TODOS os campos disponíveis)
  if (transactions && Array.isArray(transactions) && transactions.length > 0) {
    csvContent += '=== INDIVIDUAL TRANSACTIONS ===\n';
    csvContent += 'ID,Date,Type,Quantity (BTC),Price (USD),Value (USD),Daily Close (USD),RSI,Regime,Recommended Investment (USD),BTC Acquired,BTC Sold,Revenue From Sale (USD),Reinvestment With Cash (USD),Remaining Cash (USD),BTC Balance,Total Value (USD)\n';
    
    // Variáveis para calcular totais
    let totalQuantity = 0;
    let totalValue = 0;
    let totalBTCAcquired = 0;
    let totalBTCSold = 0;
    let totalRevenueFromSale = 0;
    let totalRecommendedInvestment = 0;
    let totalReinvestmentWithCash = 0;
    
    // Ordenar transações por data (mais recente primeiro)
    const sortedTransactions = [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date));
    
    sortedTransactions.forEach((tx, index) => {
      // Calcular totais
      totalQuantity += parseFloat(tx.quantity || 0);
      totalValue += parseFloat(tx.value || 0);
      totalBTCAcquired += parseFloat(tx.btc_acquired || 0);
      totalBTCSold += parseFloat(tx.btc_sold || 0);
      totalRevenueFromSale += parseFloat(tx.revenue_from_sale || 0);
      totalRecommendedInvestment += parseFloat(tx.recommended_investment || 0);
      totalReinvestmentWithCash += parseFloat(tx.reinvestment_with_cash || 0);
      
      const row = [
        tx.id || '',
        tx.date ? new Date(tx.date).toLocaleString('en-US') : '',
        tx.type || '',
        parseFloat(tx.quantity || 0).toFixed(8),
        parseFloat(tx.price || 0).toFixed(2),
        parseFloat(tx.value || 0).toFixed(2),
        parseFloat(tx.daily_close || 0).toFixed(2),
        parseFloat(tx.rsi || tx.rsi_dynamic_monthly || 0).toFixed(2),
        tx.regime || '',
        parseFloat(tx.recommended_investment || 0).toFixed(2),
        parseFloat(tx.btc_acquired || 0).toFixed(8),
        parseFloat(tx.btc_sold || 0).toFixed(8),
        parseFloat(tx.revenue_from_sale || 0).toFixed(2),
        parseFloat(tx.reinvestment_with_cash || 0).toFixed(2),
        parseFloat(tx.remaining_cash || 0).toFixed(2),
        parseFloat(tx.btc_balance || 0).toFixed(8),
        parseFloat(tx.total_value || 0).toFixed(2)
      ].join(',');
      
      csvContent += row + '\n';
    });
    
    // Adicionar linha de totais
    csvContent += '\n';
    csvContent += '=== TRANSACTION TOTALS ===\n';
    csvContent += 'Metric,Value\n';
    csvContent += `Total Transactions,${transactions.length}\n`;
    csvContent += `Total Quantity (BTC),${totalQuantity.toFixed(8)}\n`;
    csvContent += `Total Transaction Value (USD),${totalValue.toFixed(2)}\n`;
    csvContent += `Total BTC Acquired,${totalBTCAcquired.toFixed(8)}\n`;
    csvContent += `Total BTC Sold,${totalBTCSold.toFixed(8)}\n`;
    csvContent += `Net BTC Position,${(totalBTCAcquired - totalBTCSold).toFixed(8)}\n`;
    csvContent += `Total Revenue From Sales (USD),${totalRevenueFromSale.toFixed(2)}\n`;
    csvContent += `Total Recommended Investment (USD),${totalRecommendedInvestment.toFixed(2)}\n`;
    csvContent += `Total Reinvestment With Cash (USD),${totalReinvestmentWithCash.toFixed(2)}\n`;
    
    // Estatísticas por tipo de transação
    const transactionsByType = transactions.reduce((acc, tx) => {
      const type = tx.type || 'unknown';
      if (!acc[type]) {
        acc[type] = { count: 0, totalValue: 0, totalQuantity: 0 };
      }
      acc[type].count += 1;
      acc[type].totalValue += parseFloat(tx.value || 0);
      acc[type].totalQuantity += parseFloat(tx.quantity || 0);
      return acc;
    }, {});
    
    csvContent += '\n';
    csvContent += '=== TRANSACTIONS BY TYPE ===\n';
    csvContent += 'Type,Count,Total Value (USD),Total Quantity (BTC),Average Value (USD),Average Quantity (BTC)\n';
    
    Object.entries(transactionsByType).forEach(([type, stats]) => {
      const avgValue = stats.count > 0 ? (stats.totalValue / stats.count) : 0;
      const avgQuantity = stats.count > 0 ? (stats.totalQuantity / stats.count) : 0;
      csvContent += `${type.toUpperCase()},${stats.count},${stats.totalValue.toFixed(2)},${stats.totalQuantity.toFixed(8)},${avgValue.toFixed(2)},${avgQuantity.toFixed(8)}\n`;
    });
    
    // Adicionar estatísticas mensais
    const monthlyStats = transactions.reduce((acc, tx) => {
      if (!tx.date) return acc;
      
      const date = new Date(tx.date);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      
      if (!acc[monthKey]) {
        acc[monthKey] = { count: 0, totalValue: 0, totalQuantity: 0 };
      }
      
      acc[monthKey].count += 1;
      acc[monthKey].totalValue += parseFloat(tx.value || 0);
      acc[monthKey].totalQuantity += parseFloat(tx.quantity || 0);
      
      return acc;
    }, {});
    
    if (Object.keys(monthlyStats).length > 0) {
      csvContent += '\n';
      csvContent += '=== MONTHLY STATISTICS ===\n';
      csvContent += 'Month,Transactions Count,Total Value (USD),Total Quantity (BTC),Average Value (USD),Average Quantity (BTC)\n';
      
      Object.entries(monthlyStats)
        .sort(([a], [b]) => b.localeCompare(a)) // Ordenar por mês (mais recente primeiro)
        .forEach(([month, stats]) => {
          const avgValue = stats.count > 0 ? (stats.totalValue / stats.count) : 0;
          const avgQuantity = stats.count > 0 ? (stats.totalQuantity / stats.count) : 0;
          csvContent += `${month},${stats.count},${stats.totalValue.toFixed(2)},${stats.totalQuantity.toFixed(8)},${avgValue.toFixed(2)},${avgQuantity.toFixed(8)}\n`;
        });
    }
    
  } else {
    csvContent += '=== INDIVIDUAL TRANSACTIONS ===\n';
    csvContent += 'No transactions found\n';
  }
  
  return csvContent;
};

/**
 * Exporta histórico de transações para CSV
 * @param {Array} transactions - Lista de transações
 * @returns {string} Conteúdo do arquivo CSV
 */
export const exportTransactionsToCSV = (transactions) => {
  if (!transactions || !Array.isArray(transactions)) return '';
  
  let csvContent = '';
  
  csvContent += '=== TRANSACTION HISTORY EXPORT ===\n';
  csvContent += `Export Date,${new Date().toLocaleDateString('en-US')}\n`;
  csvContent += `Total Transactions,${transactions.length}\n`;
  csvContent += '\n';
  
  csvContent += '=== INDIVIDUAL TRANSACTIONS ===\n';
  csvContent += 'ID,Date,Type,Quantity (BTC),Price (USD),Value (USD),Daily Close (USD),RSI,Regime,Recommended Investment (USD),BTC Acquired,BTC Sold,Revenue From Sale (USD),Reinvestment With Cash (USD),Remaining Cash (USD),BTC Balance,Total Value (USD)\n';
  
  // Variáveis para calcular totais
  let totalQuantity = 0;
  let totalValue = 0;
  let totalBTCAcquired = 0;
  let totalBTCSold = 0;
  let totalRevenueFromSale = 0;
  let totalRecommendedInvestment = 0;
  let totalReinvestmentWithCash = 0;
  
  // Ordenar transações por data (mais recente primeiro)
  const sortedTransactions = [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date));
  
  sortedTransactions.forEach(tx => {
    // Calcular totais
    totalQuantity += parseFloat(tx.quantity || 0);
    totalValue += parseFloat(tx.value || 0);
    totalBTCAcquired += parseFloat(tx.btc_acquired || 0);
    totalBTCSold += parseFloat(tx.btc_sold || 0);
    totalRevenueFromSale += parseFloat(tx.revenue_from_sale || 0);
    totalRecommendedInvestment += parseFloat(tx.recommended_investment || 0);
    totalReinvestmentWithCash += parseFloat(tx.reinvestment_with_cash || 0);
    
    const row = [
      tx.id || '',
      tx.date ? new Date(tx.date).toLocaleString('en-US') : '',
      tx.type || '',
      parseFloat(tx.quantity || 0).toFixed(8),
      parseFloat(tx.price || 0).toFixed(2),
      parseFloat(tx.value || 0).toFixed(2),
      parseFloat(tx.daily_close || 0).toFixed(2),
      parseFloat(tx.rsi || tx.rsi_dynamic_monthly || 0).toFixed(2),
      tx.regime || '',
      parseFloat(tx.recommended_investment || 0).toFixed(2),
      parseFloat(tx.btc_acquired || 0).toFixed(8),
      parseFloat(tx.btc_sold || 0).toFixed(8),
      parseFloat(tx.revenue_from_sale || 0).toFixed(2),
      parseFloat(tx.reinvestment_with_cash || 0).toFixed(2),
      parseFloat(tx.remaining_cash || 0).toFixed(2),
      parseFloat(tx.btc_balance || 0).toFixed(8),
      parseFloat(tx.total_value || 0).toFixed(2)
    ].join(',');
    
    csvContent += row + '\n';
  });
  
  // Adicionar linha de totais
  csvContent += '\n';
  csvContent += '=== TRANSACTION TOTALS ===\n';
  csvContent += 'Metric,Value\n';
  csvContent += `Total Transactions,${transactions.length}\n`;
  csvContent += `Total Quantity (BTC),${totalQuantity.toFixed(8)}\n`;
  csvContent += `Total Transaction Value (USD),${totalValue.toFixed(2)}\n`;
  csvContent += `Total BTC Acquired,${totalBTCAcquired.toFixed(8)}\n`;
  csvContent += `Total BTC Sold,${totalBTCSold.toFixed(8)}\n`;
  csvContent += `Net BTC Position,${(totalBTCAcquired - totalBTCSold).toFixed(8)}\n`;
  csvContent += `Total Revenue From Sales (USD),${totalRevenueFromSale.toFixed(2)}\n`;
  csvContent += `Total Recommended Investment (USD),${totalRecommendedInvestment.toFixed(2)}\n`;
  csvContent += `Total Reinvestment With Cash (USD),${totalReinvestmentWithCash.toFixed(2)}\n`;
  
  // Estatísticas por tipo de transação
  const transactionsByType = transactions.reduce((acc, tx) => {
    const type = tx.type || 'unknown';
    if (!acc[type]) {
      acc[type] = { count: 0, totalValue: 0, totalQuantity: 0 };
    }
    acc[type].count += 1;
    acc[type].totalValue += parseFloat(tx.value || 0);
    acc[type].totalQuantity += parseFloat(tx.quantity || 0);
    return acc;
  }, {});
  
  csvContent += '\n';
  csvContent += '=== TRANSACTIONS BY TYPE ===\n';
  csvContent += 'Type,Count,Total Value (USD),Total Quantity (BTC),Average Value (USD),Average Quantity (BTC)\n';
  
  Object.entries(transactionsByType).forEach(([type, stats]) => {
    const avgValue = stats.count > 0 ? (stats.totalValue / stats.count) : 0;
    const avgQuantity = stats.count > 0 ? (stats.totalQuantity / stats.count) : 0;
    csvContent += `${type.toUpperCase()},${stats.count},${stats.totalValue.toFixed(2)},${stats.totalQuantity.toFixed(8)},${avgValue.toFixed(2)},${avgQuantity.toFixed(8)}\n`;
  });
  
  // Adicionar estatísticas mensais
  const monthlyStats = transactions.reduce((acc, tx) => {
    if (!tx.date) return acc;
    
    const date = new Date(tx.date);
    const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
    
    if (!acc[monthKey]) {
      acc[monthKey] = { count: 0, totalValue: 0, totalQuantity: 0 };
    }
    
    acc[monthKey].count += 1;
    acc[monthKey].totalValue += parseFloat(tx.value || 0);
    acc[monthKey].totalQuantity += parseFloat(tx.quantity || 0);
    
    return acc;
  }, {});
  
  if (Object.keys(monthlyStats).length > 0) {
    csvContent += '\n';
    csvContent += '=== MONTHLY STATISTICS ===\n';
    csvContent += 'Month,Transactions Count,Total Value (USD),Total Quantity (BTC),Average Value (USD),Average Quantity (BTC)\n';
    
    Object.entries(monthlyStats)
      .sort(([a], [b]) => b.localeCompare(a)) // Ordenar por mês (mais recente primeiro)
      .forEach(([month, stats]) => {
        const avgValue = stats.count > 0 ? (stats.totalValue / stats.count) : 0;
        const avgQuantity = stats.count > 0 ? (stats.totalQuantity / stats.count) : 0;
        csvContent += `${month},${stats.count},${stats.totalValue.toFixed(2)},${stats.totalQuantity.toFixed(8)},${avgValue.toFixed(2)},${avgQuantity.toFixed(8)}\n`;
      });
  }
  
  return csvContent;
};

/**
 * Cria um arquivo para download
 * @param {string} content - Conteúdo do arquivo
 * @param {string} filename - Nome do arquivo
 * @param {string} type - Tipo MIME do arquivo
 */
export const downloadFile = (content, filename, type = 'text/csv') => {
  const blob = new Blob([content], { type: `${type};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Simulates a fixed DCA strategy to compare with the user's dynamic strategy
 * @param {Array} transactions - User's transaction history
 * @param {number} currentBtcPrice - Current Bitcoin price
 * @param {string} username - Username for fetching portfolio data
 * @returns {Object} Comparison data for both strategies
 */
export const simulateFixedDCAStrategy = async (transactions, currentBtcPrice, username) => {
  // ✅ ROBUSTEZ: Validação de entrada aprimorada
  if (!transactions || !Array.isArray(transactions)) {
    console.error('❌ Invalid transactions data: must be an array');
    throw new Error('Invalid transactions data: must be an array');
  }
  
  if (transactions.length === 0) {
    console.error('❌ No transactions provided for simulation');
    throw new Error('No transactions provided for simulation');
  }
  
  if (!currentBtcPrice || typeof currentBtcPrice !== 'number' || currentBtcPrice <= 0) {
    console.error('❌ Invalid current BTC price:', currentBtcPrice);
    throw new Error(`Invalid current BTC price: ${currentBtcPrice}`);
  }
  
  if (!username || typeof username !== 'string' || username.trim().length === 0) {
    console.error('❌ Invalid username:', username);
    throw new Error('Username is required for DCA simulation');
  }
  
  console.log(`🚀 Starting strategy simulation with ${transactions.length} transactions and BTC price $${currentBtcPrice.toFixed(2)}`);
  
  try {
    // ✅ ROBUSTEZ: Carregar dados históricos com tratamento de erro
    const historicalData = await loadHistoricalBitcoinData();
    if (!historicalData || typeof historicalData !== 'object') {
      throw new Error('No historical data available for simulation');
    }
    
    const historicalDataCount = Object.keys(historicalData).length;
    if (historicalDataCount === 0) {
      throw new Error('Historical data is empty');
    }
    
    console.log(`✅ Loaded ${historicalDataCount} historical price points`);

    // ✅ ROBUSTEZ: Filtrar e validar transações com logging detalhado
    let invalidCount = 0;
    const validTransactions = transactions.filter(tx => {
      // Validar objeto
      if (!tx || typeof tx !== 'object') {
        invalidCount++;
        console.warn('⚠️ Skipping non-object transaction');
        return false;
      }
      
      // Validar campos obrigatórios
      const hasValue = typeof tx.value === 'number' && tx.value > 0;
      const hasDate = tx.date && !isNaN(new Date(tx.date).getTime());
      const hasPrice = typeof tx.price === 'number' && tx.price > 0;
      const hasType = tx.type && typeof tx.type === 'string';
      
      if (!hasValue || !hasDate || !hasPrice || !hasType) {
        invalidCount++;
        console.warn('⚠️ Skipping invalid transaction:', {
          id: tx.id,
          hasValue,
          hasDate,
          hasPrice,
          hasType
        });
        return false;
      }
      
      return true;
    });

    if (invalidCount > 0) {
      console.warn(`⚠️ Filtered out ${invalidCount} invalid transactions`);
    }

    if (validTransactions.length === 0) {
      throw new Error('No valid transactions found for simulation');
    }
    
    console.log(`✅ Validated ${validTransactions.length} transactions (${invalidCount} invalid filtered out)`);

    // Ordenar transações por data
    validTransactions.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calcular período total e valor total investido
    const firstTransactionDate = new Date(validTransactions[0].date);
    const today = new Date(); // ✅ CORREÇÃO: Usar data ATUAL, não última transação
    
    // Calcular valor total investido pelo usuário - CORRIGIDO para excluir vendas
    const totalInvested = validTransactions.reduce((sum, tx) => {
      const txType = tx.type?.toLowerCase();
      if (txType === 'sell') {
        // VENDAS: Não contar como investimento adicional
        return sum;
      } else if (txType === 'buy' || txType === 'reinvestment') {
        // COMPRAS: Contar como investimento
        return sum + tx.value;
      }
      return sum;
    }, 0);
    
    // ✅ CORREÇÃO CRÍTICA: Filtrar apenas operações de COMPRA
    const buyTransactions = validTransactions.filter(tx => {
      const txType = tx.type?.toLowerCase();
      return txType === 'buy' || txType === 'reinvestment';
    });
    
    // ✅ ROBUSTEZ: Proteção contra divisão por zero
    if (buyTransactions.length === 0) {
      throw new Error('No buy transactions found for simulation (only sells or other types)');
    }
    
    if (totalInvested <= 0) {
      throw new Error(`Invalid total invested amount: ${totalInvested}`);
    }
    
    // ✅ NOVA LÓGICA: Calcular número de SEMANAS entre primeira transação e HOJE
    const totalDays = Math.ceil((today - firstTransactionDate) / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.ceil(totalDays / 7);
    
    // ✅ NOVA LÓGICA: DCA fixo investe o MESMO VALOR TOTAL, dividido por TODAS as SEMANAS
    const fixedAmountPerWeek = totalInvested / totalWeeks;

    console.log(`✅ NEW Simulation parameters (WEEKLY FIXED DCA):
    - First transaction: ${firstTransactionDate.toISOString().split('T')[0]}
    - Today: ${today.toISOString().split('T')[0]}
    - Total weeks: ${totalWeeks}
    - Total invested by user: $${totalInvested.toFixed(2)}
    - Fixed DCA amount per week: $${fixedAmountPerWeek.toFixed(2)}
    - User buy transactions: ${buyTransactions.length} (some weeks may have no transactions)
    - Fixed DCA will buy EVERY week (${totalWeeks} total purchases)`);

    // ✅ NOVA LÓGICA: Criar mapa de transações do usuário por semana
    const userTransactionsByWeek = {};
    buyTransactions.forEach(tx => {
      const txDate = new Date(tx.date);
      // Calcular número da semana desde a primeira transação
      const weekNumber = Math.floor((txDate - firstTransactionDate) / (7 * 24 * 60 * 60 * 1000));
      if (!userTransactionsByWeek[weekNumber]) {
        userTransactionsByWeek[weekNumber] = [];
      }
      userTransactionsByWeek[weekNumber].push(tx);
    });

    // ✅ NOVA LÓGICA: Simular DCA fixo TODA SEMANA
    let dcaInvestment = 0;
    let dcaBitcoin = 0;
    const dcaTransactions = [];
    let successfulOperations = 0;
    let lastTransactionDayOfWeek = null; // Dia da semana da última transação
    let lastTransactionTime = null; // Hora da última transação

    // Iterar sobre TODAS as semanas desde a primeira transação até hoje
    for (let weekNumber = 0; weekNumber < totalWeeks; weekNumber++) {
      let operationDate;
      
      // Se o usuário fez transação nesta semana, usar a MESMA data/hora
      if (userTransactionsByWeek[weekNumber] && userTransactionsByWeek[weekNumber].length > 0) {
        // Usar a primeira transação da semana como referência
        const userTx = userTransactionsByWeek[weekNumber][0];
        operationDate = new Date(userTx.date);
        
        // Salvar dia da semana e hora para usar nas semanas sem transação
        lastTransactionDayOfWeek = operationDate.getDay(); // 0=domingo, 1=segunda, etc
        lastTransactionTime = {
          hours: operationDate.getHours(),
          minutes: operationDate.getMinutes(),
          seconds: operationDate.getSeconds()
        };
        
        console.log(`📅 Week ${weekNumber + 1}: User made transaction, using same date/time: ${operationDate.toISOString()}`);
      } else {
        // Usuário NÃO fez transação nesta semana
        // Usar o mesmo dia da semana e horário da última transação
        const weekStart = new Date(firstTransactionDate);
        weekStart.setDate(firstTransactionDate.getDate() + (weekNumber * 7));
        
        if (lastTransactionDayOfWeek !== null && lastTransactionTime !== null) {
          // Ajustar para o mesmo dia da semana da última transação
          const currentDayOfWeek = weekStart.getDay();
          const dayDifference = lastTransactionDayOfWeek - currentDayOfWeek;
          weekStart.setDate(weekStart.getDate() + dayDifference);
          
          // Ajustar para o mesmo horário da última transação
          weekStart.setHours(lastTransactionTime.hours);
          weekStart.setMinutes(lastTransactionTime.minutes);
          weekStart.setSeconds(lastTransactionTime.seconds);
          
          operationDate = weekStart;
          
          console.log(`📅 Week ${weekNumber + 1}: User made NO transaction, repeating last day/time: ${operationDate.toISOString()}`);
        } else {
          // Primeira semana e usuário não fez transação (caso raro)
          // Usar início da semana
          operationDate = weekStart;
          console.log(`📅 Week ${weekNumber + 1}: First week, no user transaction, using week start: ${operationDate.toISOString()}`);
        }
      }

      const operationDateStr = operationDate.toISOString().split('T')[0];

      try {
        // Buscar preço histórico para esta data (lança erro se não encontrar)
        const historicalPrice = getHistoricalPrice(operationDateStr, historicalData);
        
        if (typeof historicalPrice !== 'number' || historicalPrice <= 0) {
          throw new Error(`Invalid historical price: ${historicalPrice}`);
        }
        
        // Calcular Bitcoin comprado com o valor fixo semanal
        const bitcoinBought = fixedAmountPerWeek / historicalPrice;
        
        dcaInvestment += fixedAmountPerWeek;
        dcaBitcoin += bitcoinBought;
        successfulOperations++;
        
        dcaTransactions.push({
          date: operationDate.toISOString(),
          value: fixedAmountPerWeek,
          price: historicalPrice,
          bitcoin: bitcoinBought,
          weekNumber: weekNumber + 1,
          hadUserTransaction: !!userTransactionsByWeek[weekNumber]
        });
      } catch (error) {
        // ❌ FAIL FAST: Re-lançar erro com contexto detalhado
        throw new Error(
          `Failed to simulate DCA for week #${weekNumber + 1} on ${operationDateStr}: ${error.message}`
        );
      }
    }
    
    console.log(`✅ Fixed DCA simulation completed:
    - Total weeks simulated: ${totalWeeks}
    - Successful operations: ${successfulOperations}
    - Total invested: $${dcaInvestment.toFixed(2)}
    - Total BTC acquired: ${dcaBitcoin.toFixed(8)} BTC
    - Weeks with user transactions: ${Object.keys(userTransactionsByWeek).length}
    - Weeks without user transactions: ${totalWeeks - Object.keys(userTransactionsByWeek).length}`);

    // Calcular estratégia dinâmica do usuário - CORRIGIDO para usar Caixa real
    let userInvestment = 0;
    let userBitcoin = 0;
    let userManualSales = 0;   // Vendas manuais realizadas (apenas para informação)

    validTransactions.forEach(tx => {
      const txType = tx.type?.toLowerCase();
      
      if (txType === 'sell') {
        // VENDAS: Subtrair Bitcoin vendido E custo base proporcional
        
        // 🔥 CORREÇÃO CRÍTICA: Usar APENAS btc_sold (não usar quantity como fallback)
        const bitcoinSold = tx.btc_sold || 0;
        
        if (!tx.btc_sold && tx.quantity) {
          console.warn(`⚠️ Transaction ${tx.id} has quantity but no btc_sold. Using quantity as fallback: ${tx.quantity}`);
        }
        
        const revenue = tx.revenue_from_sale || tx.value || 0;
        const isManual = tx.is_manual || false;
        
        // 🔥 CORREÇÃO CRÍTICA: Calcular custo base proporcional ao BTC vendido
        // Se vendeu 50% do BTC, deve reduzir 50% do investimento
        const costBasisSold = userBitcoin > 0 
          ? (bitcoinSold / userBitcoin) * userInvestment 
          : 0;
        
        // Subtrair Bitcoin e custo base proporcionalmente
        userBitcoin -= bitcoinSold;
        userInvestment -= costBasisSold;
        
        // Validar que não fique negativo (proteção contra dados incorretos)
        if (userBitcoin < 0) {
          console.warn(`⚠️ Bitcoin balance went negative after sell transaction (${userBitcoin}). Resetting to 0.`);
          userBitcoin = 0;
        }
        if (userInvestment < 0) {
          console.warn(`⚠️ Investment went negative after sell transaction (${userInvestment}). Resetting to 0.`);
          userInvestment = 0;
        }
        
        console.log(`📊 SELL: Sold ${bitcoinSold.toFixed(8)} BTC, reduced investment by $${costBasisSold.toFixed(2)}`);
        
        // 📊 Vendas vão para DynamicCashTransaction (backend)
        // Não calculamos aqui, vamos buscar do backend
        
        // Manter controle de vendas manuais para informação
        if (isManual) {
          userManualSales += revenue;
        }
        
      } else if (txType === 'buy' || txType === 'reinvestment') {
        // COMPRAS: Somar investimento e Bitcoin adquirido
        userInvestment += tx.value;
        
        // 🔥 CORREÇÃO CRÍTICA: Usar btc_acquired do backend (NÃO recalcular!)
        // Backend já calculou com precisão de 8 casas decimais
        const btcAcquired = tx.btc_acquired || tx.quantity || (tx.value / tx.price);
        userBitcoin += btcAcquired;
        
        console.log(`📊 BUY: Acquired ${btcAcquired.toFixed(8)} BTC (from backend), invested $${tx.value.toFixed(2)}`);
        
        // ⚠️ Validar se backend retornou valor diferente do recalculado
        const recalculated = tx.value / tx.price;
        const diff = Math.abs(btcAcquired - recalculated);
        if (diff > 0.00000001) { // > 1 satoshi
          console.warn(`⚠️ BTC difference detected: Backend=${btcAcquired.toFixed(8)}, Recalculated=${recalculated.toFixed(8)}, Diff=${diff.toFixed(8)} BTC`);
        }
      }
      // 🗑️ REMOVIDO: portfolio_withdrawal e portfolio_deposit (conceitos obsoletos)
    });
    
    console.log(`📊 Final User Strategy: Investment=$${userInvestment.toFixed(2)}, Bitcoin=${userBitcoin.toFixed(8)} BTC`);

    // 🔥 BUSCAR Caixa DO BACKEND (via Portfolio Summary)
    // O backend já calcula e retorna dynamic_balance_sale_revenue
    let userPortfolioCash = 0; // Cash de vendas (interfere no ROI)
    
    try {
      // ✅ PADRÃO OURO: Buscar do portfolio summary (dados já calculados)
      const portfolioResponse = await api.get(`/portfolio/${username}/`, {
        params: { current_price: currentBtcPrice }
      });
      
      const saleRevenue = portfolioResponse.data?.dynamic_balance_sale_revenue;
      
      // Validar se sale revenue existe e é válido
      if (saleRevenue && typeof saleRevenue === 'number' && saleRevenue > 0) {
        userPortfolioCash = saleRevenue;
        console.log(`✅ Reports: Using sale revenue from backend: $${userPortfolioCash.toFixed(2)}`);
      } else if (saleRevenue === 0) {
        // Se for exatamente 0, é válido (usuário não vendeu nada)
        userPortfolioCash = 0;
        console.log(`📊 Reports: No sale revenue (user hasn't sold any BTC)`);
      } else {
        // Se for null, undefined ou inválido, usar fallback
        throw new Error(`Invalid sale revenue from backend: ${saleRevenue}`);
      }
    } catch (error) {
      console.warn('⚠️ Backend failed or returned invalid data, using fallback calculation:', error.message);
      
      // 🔥 FALLBACK: Calcular cash de vendas das próprias transações
      userPortfolioCash = validTransactions
        .filter(tx => tx.type?.toLowerCase() === 'sell')
        .reduce((sum, tx) => {
          const revenue = tx.revenue_from_sale || tx.value || 0;
          return sum + revenue;
        }, 0);
      
      console.log(`📊 Reports: Calculated sale revenue from transactions (fallback): $${userPortfolioCash.toFixed(2)}`);
      
      // Se fallback também retornou 0, verificar se realmente não há vendas
      const sellTransactions = validTransactions.filter(tx => tx.type?.toLowerCase() === 'sell');
      if (sellTransactions.length > 0 && userPortfolioCash === 0) {
        console.error('❌ CRITICAL: User has sell transactions but no revenue calculated!');
        console.error('Sell transactions:', sellTransactions);
      }
    }
    
    // 🔥 ALINHAMENTO COM BACKEND: Calcular valores usando MESMA metodologia
    
    // 1️⃣ DCA Fixo (simples - sem vendas)
    const dcaCurrentValue = dcaBitcoin * currentBtcPrice;
    const dcaReturn = dcaCurrentValue - dcaInvestment;
    
    if (dcaInvestment <= 0) {
      throw new Error(`Invalid DCA investment amount: ${dcaInvestment}`);
    }
    
    const dcaReturnPercentage = (dcaReturn / dcaInvestment) * 100;
    
    // 2️⃣ Estratégia Dinâmica - ALINHADO COM BACKEND
    // Backend calcula: current_cost_basis = btc_balance × avg_purchase_price
    // Aqui equivale a: userInvestment (já foi reduzido proporcionalmente nas vendas)
    
    const userBtcValue = userBitcoin * currentBtcPrice;
    
    // 📊 Valor Realizável = BTC + Cash de Vendas (IGUAL AO BACKEND)
    // Backend: realizable_value = btc_value_usd + dynamic_by_source['sale_revenue']
    const userRealizableValue = userBtcValue + userPortfolioCash;
    
    // 📊 Portfolio Total = BTC + Todo Cash (para exibição)
    const userCurrentValue = userRealizableValue; // Neste contexto, é o mesmo
    
    // 📊 ROI ALINHADO COM BACKEND:
    // Backend: total_profit = realizable_value - current_cost_basis
    // Backend: roi_percentage = (total_profit / current_cost_basis) × 100
    const currentCostBasis = userInvestment; // Custo base já ajustado por vendas
    const userReturn = userRealizableValue - currentCostBasis;
    
    if (currentCostBasis <= 0 && userBitcoin > 0) {
      // Caso especial: Vendeu mais do que tinha (lucro puro)
      console.warn(`⚠️ Cost basis is ${currentCostBasis} but user still has ${userBitcoin} BTC. This might indicate data inconsistency.`);
    }
    
    // Se vendeu tudo e não tem mais BTC, ROI é baseado apenas nas vendas
    let userReturnPercentage;
    if (userBitcoin === 0 && userPortfolioCash > 0) {
      // Vendeu tudo: ROI = (caixa recebido - total investido) / total investido
      // totalInvested = soma de todas as compras (calculado antes do loop de transações)
      console.log(`📊 User sold all BTC. ROI = (${userPortfolioCash.toFixed(2)} - ${totalInvested.toFixed(2)}) / ${totalInvested.toFixed(2)}`);
      userReturnPercentage = totalInvested > 0 ? ((userPortfolioCash - totalInvested) / totalInvested) * 100 : 0;
    } else if (currentCostBasis > 0) {
      // Caso normal: ROI = (Valor Realizável - Custo Base) / Custo Base
      userReturnPercentage = (userReturn / currentCostBasis) * 100;
    } else {
      // Edge case: sem custo base mas tem BTC (não deveria acontecer)
      userReturnPercentage = 0;
      console.warn(`⚠️ Cannot calculate ROI: cost basis is ${currentCostBasis}`);
    }
    
    console.log(`📊 ROI Calculation (ALIGNED WITH BACKEND):`);
    console.log(`   Cost Basis: $${currentCostBasis.toFixed(2)}`);
    console.log(`   BTC Value: $${userBtcValue.toFixed(2)}`);
    console.log(`   Cash (Sales): $${userPortfolioCash.toFixed(2)}`);
    console.log(`   Realizable Value: $${userRealizableValue.toFixed(2)}`);
    console.log(`   Total Return: $${userReturn.toFixed(2)}`);
    console.log(`   ROI: ${userReturnPercentage.toFixed(2)}%`);

    // ✅ Calcular preço médio de compra
    if (dcaBitcoin <= 0) {
      throw new Error(`Invalid DCA Bitcoin balance: ${dcaBitcoin}`);
    }
    if (userBitcoin < 0) { // Pode ser 0 se vendeu tudo
      throw new Error(`Invalid user Bitcoin balance: ${userBitcoin}`);
    }
    
    const dcaAveragePrice = dcaInvestment / dcaBitcoin;
    
    // 🔥 ALINHADO COM BACKEND: avg_purchase_price = current_cost_basis / btc_balance
    const userAveragePrice = userBitcoin > 0 ? currentCostBasis / userBitcoin : 0;
    
    console.log(`📊 Average Prices:`);
    console.log(`   DCA Avg Price: $${dcaAveragePrice.toFixed(2)}`);
    console.log(`   User Avg Price: $${userAveragePrice.toFixed(2)} (cost basis method)`);
    
    // ✅ Validar resultados (NaN/Infinity = ERRO)
    if (isNaN(dcaReturnPercentage) || !isFinite(dcaReturnPercentage)) {
      throw new Error(`Invalid DCA return percentage calculated: ${dcaReturnPercentage}`);
    }
    if (isNaN(userReturnPercentage) || !isFinite(userReturnPercentage)) {
      throw new Error(`Invalid user return percentage calculated: ${userReturnPercentage}`);
    }
    if (isNaN(dcaAveragePrice) || !isFinite(dcaAveragePrice) || dcaAveragePrice <= 0) {
      throw new Error(`Invalid DCA average price calculated: ${dcaAveragePrice}`);
    }

    const result = {
    summary: {
      totalWeeks,
        totalBuyOperations: buyTransactions.length,
        fixedAmountPerWeek,
        totalInvestmentPeriodDays: Math.ceil((today - firstTransactionDate) / (1000 * 60 * 60 * 24)),
        startDate: firstTransactionDate.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
        successfulOperations,
        weeksWithUserTransactions: Object.keys(userTransactionsByWeek).length,
        weeksWithoutUserTransactions: totalWeeks - Object.keys(userTransactionsByWeek).length,
      
      // Fixed DCA strategy summary
        fixedDcaBtcBalance: dcaBitcoin,
        fixedDcaTotalInvested: dcaInvestment,
        fixedDcaCurrentValue: dcaCurrentValue,
        fixedDcaProfitLoss: dcaReturn,
        fixedDcaProfitLossPercentage: dcaReturnPercentage,
        fixedDcaEfficiency: dcaInvestment > 0 ? dcaCurrentValue / dcaInvestment : 0,
      
      // Dynamic strategy summary - ATUALIZADO com portfolio cash
        dynamicBtcBalance: userBitcoin,
        dynamicBtcValue: userBtcValue,
        dynamicPortfolioCash: userPortfolioCash,
        dynamicManualSales: userManualSales,
        dynamicTotalInvested: userInvestment,
        dynamicCurrentValue: userCurrentValue,
        dynamicProfitLoss: userReturn,
        dynamicProfitLossPercentage: userReturnPercentage,
        dynamicEfficiency: userInvestment > 0 ? userCurrentValue / userInvestment : 0,
      
      // Comparison
        btcDifference: userBitcoin - dcaBitcoin,
        valueDifference: userCurrentValue - dcaCurrentValue,
        performanceDifference: (userInvestment > 0 ? userCurrentValue / userInvestment : 0) - (dcaInvestment > 0 ? dcaCurrentValue / dcaInvestment : 0)
      },
      fixedDCA: {
        totalInvested: dcaInvestment,
        totalBitcoin: dcaBitcoin,
        currentValue: dcaCurrentValue,
        totalReturn: dcaReturn,
        returnPercentage: dcaReturnPercentage,
        averagePrice: dcaAveragePrice,
        transactions: dcaTransactions
      },
      userStrategy: {
        totalInvested: userInvestment,
        totalBitcoin: userBitcoin,
        btcValue: userBtcValue,
        portfolioCash: userPortfolioCash,
        manualSales: userManualSales,
        currentValue: userCurrentValue,
        totalReturn: userReturn,
        returnPercentage: userReturnPercentage,
        averagePrice: userAveragePrice,
        transactions: validTransactions
      },
      comparison: {
        investmentDifference: dcaInvestment - userInvestment,
        bitcoinDifference: dcaBitcoin - userBitcoin,
        valueDifference: dcaCurrentValue - userCurrentValue,
        returnDifference: dcaReturn - userReturn,
        returnPercentageDifference: dcaReturnPercentage - userReturnPercentage,
        averagePriceDifference: dcaAveragePrice - userAveragePrice,
        betterStrategy: dcaReturnPercentage > userReturnPercentage ? 'Fixed DCA' : 'Dynamic Strategy'
      }
    };

    console.log('Strategy simulation completed successfully:', {
      dcaReturn: dcaReturnPercentage.toFixed(2) + '%',
      userReturn: userReturnPercentage.toFixed(2) + '%',
      winner: result.comparison.betterStrategy
    });

    return result;

  } catch (error) {
    console.error('Error in simulateFixedDCAStrategy:', error);
    throw new Error(`Strategy simulation failed: ${error.message}`);
  }
};

/**
 * Exports strategy performance comparison to CSV
 * @param {Object} strategyComparison - Strategy comparison data
 * @returns {string} CSV content
 */
export const exportStrategyPerformanceToCSV = (strategyComparison) => {
  if (!strategyComparison) return '';
  
  let csvContent = '';
  
  // Header
  csvContent += '=== STRATEGY PERFORMANCE COMPARISON ===\n';
  csvContent += `Export Date,${new Date().toLocaleDateString('en-US')}\n`;
  csvContent += `Analysis Period,${strategyComparison.summary.totalWeeks} weeks\n`;
  csvContent += `Investment Weeks,${strategyComparison.summary.investmentWeeksCount} weeks\n\n`;
  
  // Summary section
  csvContent += '=== PERFORMANCE SUMMARY ===\n';
  csvContent += 'Metric,Fixed DCA Strategy,Dynamic Strategy,Difference,Difference (%)\n';
  
  const summary = strategyComparison.summary;
  
  csvContent += `Weekly Average Investment (USD),${summary.weeklyAvgInvestment.toFixed(2)},,\n`;
  csvContent += `Total Invested (USD),${summary.fixedDCA.totalInvested.toFixed(2)},${summary.userStrategy.totalInvested.toFixed(2)},${(summary.userStrategy.totalInvested - summary.fixedDCA.totalInvested).toFixed(2)},${summary.fixedDCA.totalInvested > 0 ? ((summary.userStrategy.totalInvested - summary.fixedDCA.totalInvested) / summary.fixedDCA.totalInvested * 100).toFixed(2) : 0}%\n`;
  csvContent += `BTC Balance,${summary.fixedDCA.totalBitcoin.toFixed(8)},${summary.userStrategy.totalBitcoin.toFixed(8)},${summary.btcDifference.toFixed(8)},${summary.fixedDCA.totalBitcoin > 0 ? (summary.btcDifference / summary.fixedDCA.totalBitcoin * 100).toFixed(2) : 0}%\n`;
  csvContent += `Current Value (USD),${summary.fixedDCA.currentValue.toFixed(2)},${summary.userStrategy.currentValue.toFixed(2)},${summary.valueDifference.toFixed(2)},${summary.fixedDCA.currentValue > 0 ? (summary.valueDifference / summary.fixedDCA.currentValue * 100).toFixed(2) : 0}%\n`;
  csvContent += `Profit/Loss (USD),${summary.fixedDCA.totalReturn.toFixed(2)},${summary.userStrategy.totalReturn.toFixed(2)},${(summary.userStrategy.totalReturn - summary.fixedDCA.totalReturn).toFixed(2)},${Math.abs(summary.fixedDCA.totalReturn) > 0 ? ((summary.userStrategy.totalReturn - summary.fixedDCA.totalReturn) / Math.abs(summary.fixedDCA.totalReturn) * 100).toFixed(2) : 0}%\n`;
  csvContent += `Profit/Loss (%),${summary.fixedDCA.returnPercentage.toFixed(2)}%,${summary.userStrategy.returnPercentage.toFixed(2)}%,${(summary.userStrategy.returnPercentage - summary.fixedDCA.returnPercentage).toFixed(2)}%,\n`;
  csvContent += `Efficiency Ratio,${summary.comparison.performanceDifference.toFixed(4)},${summary.comparison.performanceDifference > 0 ? 'Dynamic Strategy' : 'Fixed DCA Strategy'}\n\n`;
  
  // Risk metrics section
  csvContent += '=== RISK METRICS ===\n';
  csvContent += 'Metric,Fixed DCA Strategy,Dynamic Strategy,Difference\n';
  csvContent += `Volatility (%),${summary.comparison.volatilityDifference.toFixed(2)}%\n`;
  csvContent += `Maximum Drawdown (%),${summary.comparison.maxDrawdownDifference.toFixed(2)}%\n`;
  csvContent += `Sharpe Ratio,${summary.comparison.sharpeRatioDifference.toFixed(4)}\n`;
  csvContent += `Win Rate (%),${summary.comparison.winRateDifference.toFixed(2)}%\n`;
  csvContent += `Average Weekly Return (%),${summary.comparison.avgReturnDifference.toFixed(4)}%\n`;
  csvContent += `Risk-Adjusted Return,${summary.comparison.riskAdjustedReturnDifference.toFixed(4)}\n\n`;
  
  // Performance analysis
  csvContent += '=== PERFORMANCE ANALYSIS ===\n';
  const betterStrategy = summary.comparison.betterStrategy === 'Dynamic Strategy' ? 'Dynamic Strategy' : 'Fixed DCA Strategy';
  const efficiencyImprovement = summary.comparison.performanceDifference > 0 ? Math.abs(summary.comparison.performanceDifference / summary.fixedDCA.totalInvested * 100) : 0;
  const riskProfile = summary.comparison.volatilityDifference > 0 ? 'Higher Risk' : 'Lower Risk';
  const riskAdjustedWinner = summary.comparison.riskAdjustedReturnDifference > 0 ? 'Dynamic Strategy' : 'Fixed DCA Strategy';
  
  csvContent += `Better Strategy (Efficiency),${betterStrategy}\n`;
  csvContent += `Efficiency Improvement,${efficiencyImprovement.toFixed(2)}%\n`;
  csvContent += `Dynamic Strategy Risk Profile,${riskProfile}\n`;
  csvContent += `Better Risk-Adjusted Returns,${riskAdjustedWinner}\n`;
  csvContent += `Consistency (Win Rate Difference),${summary.comparison.winRateDifference > 0 ? 'More Consistent' : 'Less Consistent'}\n\n`;
  
  // Timeline data
  csvContent += '=== WEEKLY PERFORMANCE TIMELINE ===\n';
  csvContent += 'Week,Date,BTC Price (USD),Fixed DCA BTC Balance,Fixed DCA Total Invested (USD),Fixed DCA Value (USD),Fixed DCA P/L (%),Dynamic BTC Balance,Dynamic Total Invested (USD),Dynamic Value (USD),Dynamic P/L (%),BTC Difference,Value Difference (USD)\n';
  
  strategyComparison.fixedDCA.transactions.forEach((tx, index) => {
    const week = index + 1;
    const weekDate = new Date(tx.date);
    const weekDateStr = weekDate.toISOString().split('T')[0];
    const weekPrice = tx.price;
    const btcBought = tx.bitcoin;
    const dcaValue = btcBought * weekPrice;
    const dcaReturn = dcaValue - tx.value;
    const dcaReturnPercentage = (dcaReturn / tx.value) * 100;
    const dcaDrawdown = (dcaValue - tx.value) / dcaValue * 100;

    csvContent += [
      week,
      weekDateStr,
      weekPrice.toFixed(2),
      btcBought.toFixed(8),
      tx.value.toFixed(2),
      dcaValue.toFixed(2),
      dcaReturnPercentage.toFixed(2),
      btcBought.toFixed(8),
      tx.value.toFixed(2),
      dcaValue.toFixed(2),
      dcaReturnPercentage.toFixed(2),
      (btcBought - btcBought).toFixed(8),
      (dcaValue - tx.value).toFixed(2)
    ].join(',') + '\n';
  });
  
  // Key insights section
  csvContent += '\n=== KEY INSIGHTS ===\n';
  csvContent += 'Insight,Value\n';
  
  if (summary.comparison.performanceDifference > 0) {
    csvContent += `Strategy Effectiveness,Dynamic strategy outperformed by ${efficiencyImprovement.toFixed(2)}%\n`;
  } else {
    csvContent += `Strategy Effectiveness,Fixed DCA would have been better by ${efficiencyImprovement.toFixed(2)}%\n`;
  }
  
  if (summary.comparison.volatilityDifference > 0) {
    csvContent += `Risk Management,Dynamic strategy achieved lower volatility\n`;
  } else {
    csvContent += `Risk Management,Dynamic strategy had higher volatility\n`;
  }
  
  if (summary.comparison.winRateDifference > 0) {
    csvContent += `Consistency,Dynamic strategy was more consistent\n`;
  } else {
    csvContent += `Consistency,Fixed DCA would have been more consistent\n`;
  }
  
  if (summary.comparison.maxDrawdownDifference > 0) {
    csvContent += `Downside Protection,Dynamic strategy had better downside protection\n`;
  } else {
    csvContent += `Downside Protection,Fixed DCA would have had better downside protection\n`;
  }
  
  return csvContent;
};

/**
 * Calcula métricas avançadas de performance para análise detalhada
 * @param {Array} timelineData - Dados da timeline de performance
 * @param {Object} summary - Resumo da comparação de estratégias
 * @returns {Object} Métricas avançadas
 */
export const calculateAdvancedPerformanceMetrics = (timelineData, summary) => {
  if (!timelineData || timelineData.length === 0) {
    return {};
  }

  try {
    // Calcular métricas de consistência
    const fixedDcaValues = timelineData.map(week => week.fixedDCA.currentValue);
    const dynamicValues = timelineData.map(week => week.userStrategy.currentValue);
    
    // Calcular correlação entre as estratégias
    const correlation = calculateCorrelation(fixedDcaValues, dynamicValues);
    
    // Calcular beta (sensibilidade da estratégia dinâmica em relação à fixa)
    const beta = calculateBeta(fixedDcaValues, dynamicValues);
    
    // Calcular Information Ratio
    const trackingError = calculateTrackingError(fixedDcaValues, dynamicValues);
    const informationRatio = trackingError > 0 ? (summary.comparison.performanceDifference / trackingError) : 0;
    
    // Calcular Calmar Ratio (retorno anualizado / max drawdown)
    const calmarRatioFixed = summary.comparison.maxDrawdownDifference > 0 ? 
      (summary.comparison.returnPercentageDifference / summary.comparison.maxDrawdownDifference) : 0;
    const calmarRatioDynamic = summary.comparison.maxDrawdownDifference > 0 ? 
      (summary.comparison.returnPercentageDifference / summary.comparison.maxDrawdownDifference) : 0;
    
    // Calcular Sortino Ratio (similar ao Sharpe, mas usando apenas volatilidade negativa)
    const sortinoRatioFixed = calculateSortinoRatio(timelineData.map(w => w.fixedDCA.returnPercentage));
    const sortinoRatioDynamic = calculateSortinoRatio(timelineData.map(w => w.userStrategy.returnPercentage));
    
    // Calcular Value at Risk (VaR) - 5% worst case
    const varFixed = calculateVaR(timelineData.map(w => w.fixedDCA.returnPercentage), 0.05);
    const varDynamic = calculateVaR(timelineData.map(w => w.userStrategy.returnPercentage), 0.05);
    
    // Calcular máximo período de drawdown
    const maxDrawdownPeriodFixed = calculateMaxDrawdownPeriod(timelineData.map(w => w.fixedDCA.returnPercentage));
    const maxDrawdownPeriodDynamic = calculateMaxDrawdownPeriod(timelineData.map(w => w.userStrategy.returnPercentage));
    
    return {
      correlation: correlation,
      beta: beta,
      trackingError: trackingError,
      informationRatio: informationRatio,
      calmarRatio: {
        fixed: calmarRatioFixed,
        dynamic: calmarRatioDynamic,
        difference: calmarRatioDynamic - calmarRatioFixed
      },
      sortinoRatio: {
        fixed: sortinoRatioFixed,
        dynamic: sortinoRatioDynamic,
        difference: sortinoRatioDynamic - sortinoRatioFixed
      },
      valueAtRisk: {
        fixed: varFixed,
        dynamic: varDynamic,
        difference: varDynamic - varFixed
      },
      maxDrawdownPeriod: {
        fixed: maxDrawdownPeriodFixed,
        dynamic: maxDrawdownPeriodDynamic,
        difference: maxDrawdownPeriodDynamic - maxDrawdownPeriodFixed
      }
    };
  } catch (error) {
    console.error('Error calculating advanced performance metrics:', error);
    return {};
  }
};

/**
 * Calcula correlação entre duas séries de dados
 */
const calculateCorrelation = (series1, series2) => {
  if (series1.length !== series2.length || series1.length === 0) return 0;
  
  const mean1 = series1.reduce((sum, val) => sum + val, 0) / series1.length;
  const mean2 = series2.reduce((sum, val) => sum + val, 0) / series2.length;
  
  let numerator = 0;
  let sum1Sq = 0;
  let sum2Sq = 0;
  
  for (let i = 0; i < series1.length; i++) {
    const diff1 = series1[i] - mean1;
    const diff2 = series2[i] - mean2;
    numerator += diff1 * diff2;
    sum1Sq += diff1 * diff1;
    sum2Sq += diff2 * diff2;
  }
  
  const denominator = Math.sqrt(sum1Sq * sum2Sq);
  return denominator > 0 ? numerator / denominator : 0;
};

/**
 * Calcula beta (sensibilidade)
 */
const calculateBeta = (benchmark, portfolio) => {
  if (benchmark.length !== portfolio.length || benchmark.length === 0) return 1;
  
  const benchmarkReturns = [];
  const portfolioReturns = [];
  
  for (let i = 1; i < benchmark.length; i++) {
    if (benchmark[i - 1] > 0 && portfolio[i - 1] > 0) {
      benchmarkReturns.push((benchmark[i] / benchmark[i - 1]) - 1);
      portfolioReturns.push((portfolio[i] / portfolio[i - 1]) - 1);
    }
  }
  
  if (benchmarkReturns.length === 0) return 1;
  
  const correlation = calculateCorrelation(benchmarkReturns, portfolioReturns);
  const benchmarkStd = calculateStandardDeviation(benchmarkReturns);
  const portfolioStd = calculateStandardDeviation(portfolioReturns);
  
  return benchmarkStd > 0 ? correlation * (portfolioStd / benchmarkStd) : 1;
};

/**
 * Calcula tracking error
 */
const calculateTrackingError = (benchmark, portfolio) => {
  if (benchmark.length !== portfolio.length || benchmark.length === 0) return 0;
  
  const differences = [];
  for (let i = 0; i < benchmark.length; i++) {
    differences.push(portfolio[i] - benchmark[i]);
  }
  
  return calculateStandardDeviation(differences);
};

/**
 * Calcula desvio padrão
 */
const calculateStandardDeviation = (values) => {
  if (values.length === 0) return 0;
  
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  
  return Math.sqrt(variance);
};

/**
 * Calcula Sortino Ratio
 */
const calculateSortinoRatio = (returns) => {
  if (returns.length === 0) return 0;
  
  const meanReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
  const negativeReturns = returns.filter(ret => ret < 0);
  
  if (negativeReturns.length === 0) return meanReturn > 0 ? Infinity : 0;
  
  const downwardDeviation = Math.sqrt(
    negativeReturns.reduce((sum, ret) => sum + Math.pow(ret, 2), 0) / negativeReturns.length
  );
  
  return downwardDeviation > 0 ? meanReturn / downwardDeviation : 0;
};

/**
 * Calcula Value at Risk (VaR)
 */
const calculateVaR = (returns, confidence) => {
  if (returns.length === 0) return 0;
  
  const sortedReturns = [...returns].sort((a, b) => a - b);
  const index = Math.floor(returns.length * confidence);
  
  return sortedReturns[index] || 0;
};

/**
 * Calcula período máximo de drawdown
 */
const calculateMaxDrawdownPeriod = (drawdowns) => {
  if (drawdowns.length === 0) return 0;
  
  let maxPeriod = 0;
  let currentPeriod = 0;
  
  for (const drawdown of drawdowns) {
    if (drawdown > 0) {
      currentPeriod++;
      maxPeriod = Math.max(maxPeriod, currentPeriod);
    } else {
      currentPeriod = 0;
    }
  }
  
  return maxPeriod;
};

/**
 * Gera relatório de performance detalhado com todas as métricas
 * @param {Object} strategyComparison - Dados da comparação de estratégias
 * @returns {Object} Relatório detalhado
 */
export const generateDetailedPerformanceReport = (strategyComparison) => {
  if (!strategyComparison) {
    return null;
  }
  
  const advancedMetrics = calculateAdvancedPerformanceMetrics(
    strategyComparison.fixedDCA.transactions, 
    strategyComparison.summary
  );
  
  return {
    ...strategyComparison,
    advancedMetrics,
    reportGenerated: new Date().toISOString(),
    dataQuality: {
      totalWeeks: strategyComparison.summary.totalWeeks,
      investmentWeeks: strategyComparison.summary.investmentWeeksCount,
      dataCompleteness: (strategyComparison.summary.investmentWeeksCount / strategyComparison.summary.totalWeeks) * 100,
      historicalDataPoints: strategyComparison.fixedDCA.transactions.length
    }
  };
};

const reportService = {
  getPerformanceReport,
  exportPortfolioToCSV,
  exportCompletePortfolioToCSV,
  exportTransactionsToCSV,
  downloadFile,
  simulateFixedDCAStrategy,
  exportStrategyPerformanceToCSV,
  clearHistoricalDataCache,
  calculateAdvancedPerformanceMetrics,
  generateDetailedPerformanceReport,
  createContinuousTimeline,
  loadHistoricalBitcoinData
};

// ✅ Exportações nomeadas para uso direto
export { createContinuousTimeline, loadHistoricalBitcoinData };

export default reportService; 